import { useCallback, useEffect, useRef, useState } from 'react';

const LoadingStates = {
  initial: 0,
  loading: 1,
  loaded: 2,
};

export function useAudioPlayer() {
  const [loadingState, setLoadingState] = useState(LoadingStates.initial);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioCtxRef = useRef(null);
  const sourceRef = useRef(null);
  const bufferRef = useRef(null);
  const startTimeRef = useRef(0);
  const pauseOffsetRef = useRef(0);
  const finishCallbackRef = useRef(null);
  const reqAnimFrameRef = useRef(null);
  const scriptNodeRef = useRef(null);
  const queueRef = useRef([]);

  const initAudioContext = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
    }
    if (!scriptNodeRef.current) {
      const ctx = audioCtxRef.current;
      const node = ctx.createScriptProcessor(4096, 0, 1);
      node.onaudioprocess = (e) => {
        const output = e.outputBuffer.getChannelData(0);
        let i = 0;
        while (i < output.length) {
          if (queueRef.current.length === 0) {
            output[i++] = 0;
            continue;
          }
          const chunk = queueRef.current[0];
          output[i++] = chunk.shift() / 32768; 
          if (chunk.length === 0) queueRef.current.shift();
        }
      };
      node.connect(ctx.destination);
      scriptNodeRef.current = node;
    }
  };

  const play = async (url, onFinish) => {
    setIsFinished(false);
    initAudioContext();
    finishCallbackRef.current = onFinish;
    setLoadingState(LoadingStates.loading);

    const resp = await fetch(url);
    const arrayBuffer = await resp.arrayBuffer();
    const audioBuffer = await audioCtxRef.current.decodeAudioData(arrayBuffer);
    bufferRef.current = audioBuffer;
    setDuration(audioBuffer.duration);
    setLoadingState(LoadingStates.loaded);

    const source = audioCtxRef.current.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioCtxRef.current.destination);
    source.onended = () => {
      setIsPlaying(false);
      if (finishCallbackRef.current && !pauseOffsetRef.current) {
        finishCallbackRef.current();
      }
    };
    sourceRef.current = source;

    startTimeRef.current = audioCtxRef.current.currentTime;
    source.start(0, pauseOffsetRef.current);
    setIsPlaying(true);
  };

  const playChunk = (chunk) => {
    initAudioContext();
    setLoadingState(LoadingStates.loaded);
    let int16;
    if (typeof chunk === 'string') {
      /* decode base64 → Int16Array */
      const buf = Buffer.from(chunk, 'base64');
      int16 = new Int16Array(buf.buffer, buf.byteOffset, buf.length / 2);
    } else {
      int16 = new Int16Array(chunk);
    }
    queueRef.current.push(Array.from(int16));
    setIsPlaying(true);
  };

  const updateTime = useCallback(() => {
    const elapsed = audioCtxRef.current.currentTime - startTimeRef.current + pauseOffsetRef.current;

    if (elapsed >= duration) {
      setIsPlaying(false);
      setIsFinished(true);

      return;
    }

    setCurrentTime(Math.min(elapsed, duration));
    reqAnimFrameRef.current = requestAnimationFrame(updateTime);
  }, [duration]);

  const pause = () => {
    if (audioCtxRef.current && sourceRef.current) {
      pauseOffsetRef.current += audioCtxRef.current.currentTime - startTimeRef.current;
      sourceRef.current.stop();
      setIsPlaying(false);
    }
    if (reqAnimFrameRef.current) {
      cancelAnimationFrame(reqAnimFrameRef.current);
      reqAnimFrameRef.current = null;
    }
  };

  const resume = () => {
    if (bufferRef.current) {
      playFromOffset();
    }
  };

  const playFromOffset = () => {
    const source = audioCtxRef.current.createBufferSource();
    source.buffer = bufferRef.current;
    source.connect(audioCtxRef.current.destination);
    source.onended = () => {
      setIsPlaying(false);
      if (finishCallbackRef.current && !pauseOffsetRef.current) {
        finishCallbackRef.current();
      }
    };
    sourceRef.current = source;
    startTimeRef.current = audioCtxRef.current.currentTime;
    source.start(0, pauseOffsetRef.current);
    setIsPlaying(true);
  };

  const stop = () => {
    setIsPlaying(false);
    pauseOffsetRef.current = 0;
    finishCallbackRef.current = null;
    bufferRef.current = null;
    queueRef.current = [];
    setCurrentTime(0);
    if (sourceRef.current) {
      sourceRef.current.stop();
    }
    if (reqAnimFrameRef.current) {
      cancelAnimationFrame(reqAnimFrameRef.current);
      reqAnimFrameRef.current = null;
    }
  };

  useEffect(() => {
    // cleanup
    return () => stop();
  }, []);

  useEffect(() => {
    if (isPlaying) {
      reqAnimFrameRef.current = requestAnimationFrame(updateTime);
    }
  }, [isPlaying, updateTime]);

  return {
    isLoading: loadingState === LoadingStates.loading,
    isFinished,
    isPlaying,
    duration,
    currentTime,
    play,
    pause,
    resume,
    stop,
    playChunk,
  };
}
